/* eslint-disable react/prop-types */
import React from "react";
import { Box, Text } from "@chakra-ui/react";
import { useQuery } from "@apollo/client";
import { Select as ChakraReactSelect } from "chakra-react-select";

import { JOB_OWNER_OPTIONS_QUERY } from "../graphql/jobs_admin";
import { ownerName } from "../helpers/jobOwner";

export const JOB_OWNER_HELP_TEXT =
  "The person this job belongs to. They get its Mya thread and are shown to hired candidates as the agency contact.";

function toOption(owner) {
  return { value: owner.id, label: ownerName(owner), owner };
}

/**
 * The select options: everyone the API says the caller can choose, plus the
 * current owner when they are not among them (for example a Moblyze staff
 * member who owns a job in an agency they switched into), so the selected
 * value always shows.
 */
export function jobOwnerSelectOptions(ownerOptions, value) {
  const options = (ownerOptions || []).map(toOption);
  if (value?.id && !options.some((o) => o.value === value.id)) {
    options.unshift(toOption(value));
  }
  return options;
}

function formatOptionLabel(option, { context }) {
  if (context === "value") {
    return option.label;
  }
  return (
    <Box>
      <Text>{option.label}</Text>
      {option.owner?.email && (
        <Text fontSize="xs" color="gray.400">
          {option.owner.email}
        </Text>
      )}
    </Box>
  );
}

/**
 * Picks a job's owner from jobOwnerOptions. `value` and `onChange` deal in
 * JobOwner objects ({ id, firstName, lastName, email, role }).
 */
function JobOwnerSelect({
  value,
  onChange,
  inputId = "jobOwner",
  placeholder = "Select owner",
  isDisabled = false,
  onBlur,
}) {
  const { data, loading, error } = useQuery(JOB_OWNER_OPTIONS_QUERY);

  const options = React.useMemo(
    () => jobOwnerSelectOptions(data?.jobOwnerOptions, value),
    [data, value]
  );
  const selected = options.find((o) => o.value === value?.id) || null;

  return (
    <>
      <ChakraReactSelect
        inputId={inputId}
        options={options}
        value={selected}
        onChange={(opt) => onChange(opt?.owner || null)}
        onBlur={onBlur}
        formatOptionLabel={formatOptionLabel}
        placeholder={placeholder}
        isLoading={loading}
        isDisabled={isDisabled}
        colorScheme="blue"
        variant="outlineDark"
      />
      {error && (
        <Text fontSize="sm" color="orange.300" mt={1}>
          Could not load the people who can own this job.
        </Text>
      )}
    </>
  );
}

export { JobOwnerSelect };
export default JobOwnerSelect;
